import { type ILiveValuesTransformEvent } from '@framework/validation/live/LiveTypes.ts';
import * as React from 'react';
import { useLayoutEffect, useMemo, useRef } from 'react';
import { type AnyObject, type ObjectSchema } from 'yup';
import {
  type IResolvedSchemaStore,
  ResolvedSchemaStore,
} from '../../resolved-schema/ResolvedSchemaStore.ts';
import { type IFormTransformOnSubmitFn } from '../IFormTransformOnSubmitFn.ts';
import {
  createInvalidFormSubject,
  type InvalidFormSubject,
} from './InvalidFormSubject.tsx';

export interface IFormAugmentedContext {
  schema: ObjectSchema<AnyObject>;
  validationContext?: any;
  resolvedStore: IResolvedSchemaStore;
  /**
   * Subject per comunicare l'evento di Submit con i risultati di validazione
   */
  invalidSubject: InvalidFormSubject;

  /**
   * Funzione che permette di inviare il form.
   * Permette di chiamare programmaticamente la onSubmit.
   */
  submitForm: React.MutableRefObject<
    | null
    | ((
        event?: React.BaseSyntheticEvent,
        transformOnSubmit?: IFormTransformOnSubmitFn,
      ) => Promise<{
        /**
         * Valore di ritorno della funzione di submit passata al form
         */
        result: any;
        /**
         * Valori originali passati alla onSubmit del form
         */
        originalValue: any;
      }>)
  >;

  /**
   * Funzione di onSubmit del form (a differenza di submitForm non attiva la validazione dei campi)
   */
  onSubmit: React.MutableRefObject<
    null | ((data: any, e: any) => any | Promise<any>)
  >;

  /**
   * Ref condivisa per deduplicare le chiamate di `useTriggerComputedSync`
   * all'interno del form.
   * Garantisce che chiamate multiple da hook/componenti diversi nello stesso
   * ciclo sincrono producano un _solo_ `syncComputedValues`.
   */
  computedSyncScheduled: React.MutableRefObject<boolean>;

  /**
   * Coda degli eventi che hanno richiesto `syncComputedValues` nel ciclo
   * schedulato. Usata dalle values transform per conoscere i campi sorgente.
   * Gli eventi sono deduplicati per `path`: un path ri-modificato viene
   * rimosso e riaccodato in fondo (vince l'ultimo valore).
   */
  computedSyncEvents: React.MutableRefObject<ILiveValuesTransformEvent[]>;

  /**
   * Valori iniziali del form. Usati da `syncComputedValues` per il dirty
   * check: RHF cancella `_defaultValues` durante l'unregister dei campi
   * stripped, quindi non è affidabile per il confronto.
   */
  initialValues?: any;
}

export const FormAugmentedContext = React.createContext<IFormAugmentedContext>(
  null as any,
);

/**
 * Crea il context aumentato del Form corrente.
 * Utilizziamo la memo per evitare che il context aumentato venga ricreato
 * ad ogni render.
 */
export function FormAugmentedContextProvider(props: {
  schema: ObjectSchema<AnyObject>;
  validationContext?: any;
  initialValues?: any;
  children: React.ReactNode;
}) {
  const { schema, validationContext, initialValues, children } = props;

  const resolvedStoreRef = useRef(new ResolvedSchemaStore());
  const submitForm = useRef(null as any);
  const onSubmit = useRef(null as any);
  const computedSyncScheduled = useRef(false);
  const computedSyncEvents = useRef<ILiveValuesTransformEvent[]>([]);

  // Subject per comunicare l'evento di Submit con i risultati di validazione
  const invalidSubject = useRef(createInvalidFormSubject());

  // A) Eager init: risolve tutti gli schema al mount e quando schema/ctx cambiano
  // NON possiamo usare `useEffect` perché ci serve che i campi figli abbiano
  // accesso allo state risolto al primo render per evitare glitch di
  // visibilità/editabilità
  useMemo(() => {
    resolvedStoreRef.current.resolve(schema, initialValues, validationContext);
  }, [schema, validationContext, initialValues]);

  // B) Notifica i subscriber dopo il commit DOM, prima del paint.
  // Separato dal useMemo per evitare state update nei figli durante il render.
  useLayoutEffect(() => {
    resolvedStoreRef.current.flushNotifications();
  }, [schema, validationContext, initialValues]);

  const value = useMemo(
    () => ({
      schema,
      initialValues,
      resolvedStore: resolvedStoreRef.current,
      validationContext,
      invalidSubject: invalidSubject.current,
      submitForm,
      onSubmit,
      computedSyncScheduled,
      computedSyncEvents,
    }),
    [schema, validationContext, initialValues],
  );

  return (
    <FormAugmentedContext.Provider value={value}>
      {children}
    </FormAugmentedContext.Provider>
  );
}
/**
 * Ottiene il context aumentato del Form in cui ci troviamo
 */
export function useFormAugmentedContext() {
  return React.useContext(FormAugmentedContext);
}
